// External
import _ from "lodash";

// Relative
import classes from "../DynamicPromptInput.scss";

// Constants
export const CUSTOM_INSTRUCTIONS_ID = "CUSTOM_INSTRUCTIONS_ID";
export const ADDITIONAL_INSTRUCTIONS_ID = "ADDITIONAL_INSTRUCTIONS_ID";
export const EDITABLE_CONTENT = "EDITABLE_CONTENT";
export const FIELD_SPACER = "FIELD_SPACER";

/**
 * @param {*} contentEditableRef
 * @param {*} callback
 */
export const loopingChildNodes = (contentEditableRef, callback) => {
  const childNodes = _.get(
    contentEditableRef?.current,
    "childNodes.0.childNodes",
    []
  );
  _.forEach(childNodes, element => {
    const elementID = element?.id;
    if (elementID === FIELD_SPACER) return;
    callback(element);
  });
};

/**
 * @param {*} label
 * @param {*} defaultValues
 * @returns Html string with placeholder and editable span
 */
const withPlaceholderAndEditableSpan = (label, defaultValues = []) => {
  // * Regex to match #...#
  const placeholderPattern = /(#)\s*(.*?)\s*\1/g;

  // * Replace placeholders by span
  let count = 0;
  const updatedLabel = _.replace(
    label,
    placeholderPattern,
    (match, hashes, content) => {
      if (hashes === "#") {
        const defaultValue = defaultValues[count] || "";
        const id = `${EDITABLE_CONTENT}_${count}`;
        count++;
        return `<span id="${id}" class="${classes.placeholderSpan}" role="textbox" contenteditable="true" placeholder="${content}">${defaultValue}</span>`;
      }
    }
  );

  return updatedLabel;
};

/**
 * @param {*} contentEditableRef
 * @returns Serialized data from contentEditableRef
 */
export const getSerializedData = contentEditableRef => {
  // * Returning when No element or no childNodes
  const element = contentEditableRef.current;
  const childNodes = _.get(element, "childNodes.0.childNodes", []);
  if (!element || _.isEmpty(childNodes)) return;

  let position = 0;
  const serializedData = [];

  loopingChildNodes(contentEditableRef, element => {
    const elementID = element?.id;
    const data = element?.data || _.get(element, "innerText");

    // * Only allowing valid data to save in metadata, Removing hash from input
    if (elementID || _.trim(data)) {
      serializedData.push({
        key: elementID,
        index: position,
        data: (_.trim(data) || "").replace(/#/g, ""),
      });
      position++;
    }
  });

  return serializedData;
};

/**
 * @param {*} t
 * @param {*} instruction
 * @param {*} selectedPrompt
 * @returns Json to html string
 */
export const getInstructionDataHtmlString = (
  t,
  instruction,
  selectedPrompt
) => {
  const PLACEHOLDER = t("ai:ai_tutor_prompt_selected_placeholder");
  const START = '<span contenteditable="false" style="user-select: auto;">';
  const END = "</span>";

  const instructionData = _.get(instruction, "data", []);

  // * Getting additional instruction data
  const additionalInstructionData = _.find(instructionData, {
    key: ADDITIONAL_INSTRUCTIONS_ID,
  });
  const additionalInstructionValue =
    _.get(additionalInstructionData, "data", "") || "";

  // * Getting preset prompt other editable data
  const editableContents = _.filter(instructionData, item => {
    return _.includes(item?.key, EDITABLE_CONTENT);
  });
  const editableContentsData = _.reduce(
    editableContents,
    (acc, item, index) => {
      acc[index] = _.get(item, "data", "");
      return acc;
    },
    {}
  );

  const additionalInstructionSpan = `<div><span id="${ADDITIONAL_INSTRUCTIONS_ID}" placeholder="${PLACEHOLDER}" class="${classes.additionalInstructionSpan}" role="textbox" tabindex="0" contenteditable="true">${additionalInstructionValue}</span></div>`;
  const instructionSpan = withPlaceholderAndEditableSpan(
    selectedPrompt,
    editableContentsData
  );
  return `${START}${instructionSpan} ${additionalInstructionSpan}${END}`;
};

/**
 * @param {*} e
 * @param {*} contentEditableRef
 * Focuses the content editable area specified by the event's target id,
 * or falls back to the default ADDITIONAL_INSTRUCTIONS_ID if not present.
 */
export const focusEditableArea = (e, contentEditableRef) => {
  const targetElementId = e?.target?.id || ADDITIONAL_INSTRUCTIONS_ID;

  const focusElement = contentEditableRef?.current?.querySelector(
    `[id="${targetElementId}"]`
  );
  focusElement?.focus();
};

/**
 * @param {*} contentEditableRef
 * @returns Array of editable elements within the contentEditableRef
 */
export const getEditableElements = contentEditableRef => {
  const root = contentEditableRef?.current;
  if (!root) return [];
  const nodeList = root.querySelectorAll(
    `[id^="${EDITABLE_CONTENT}"] , [id="${ADDITIONAL_INSTRUCTIONS_ID}"]`
  );
  return Array.from(nodeList);
};

/**
 * @param {*} element
 * @returns { pos: number, total: number } - The position of the caret and total length of the element's text content
 * This function calculates the caret position within a given element.
 */
const getCaretOffsetWithin = element => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return { pos: 0, total: 0 };
  const range = selection.getRangeAt(0);
  if (!element.contains(range.commonAncestorContainer))
    return { pos: 0, total: 0 };
  if (!range.collapsed) return { pos: 0, total: 0 };

  // Build a range from start of element to caret
  const preCaretRange = document.createRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.endContainer, range.endOffset);

  const pos = preCaretRange.toString().length;
  const total = (element.textContent || "").length;
  return { pos, total };
};

/**
 * @param {*} element
 * @param {string} edge - "start" or "end"
 * @returns {boolean}
 * Checks if the caret is at the start or end of the element.
 */
export const isCaretAtEdge = (element, edge = "end") => {
  try {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const { pos, total } = getCaretOffsetWithin(element);
    if (edge === "start") return pos === 0;
    return pos === total;
  } catch (err) {
    return false;
  }
};

/**
 * @param {*} el - The element to focus
 * @param {string} position - "start" or "end"
 * Focuses the element and places the caret at the specified position.
 */
export const focusElementWithCaret = (el, position = "start") => {
  if (!el) return;
  el.focus();
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(position === "start" ? true : false);
  selection.removeAllRanges();
  selection.addRange(range);
};
